/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import textStyles from '../../foundations/textStyles'

export default {
    baseStyle: ({colorScheme: c, theme}) => {
        if (!theme.colors[c]?.bright) {
            c = null
        }
        return {
            overlay: {
                bg: 'rgba(0, 0, 0, 0.6)'
            },
            dialog: {
                borderRadius: 'none',
                bg: c ? `${c}.bright` : 'stokkeCore.white',
                boxShadow: 'none',
                my: 20
            },
            header: {
                px: {base: 4, lg: 6, xl: 8},
                py: null,
                color: c ? `${c}.dark` : 'stokkeCore.black'
            },
            closeButton: {
                // new specification for close button
                // see https://dept-de.atlassian.net/browse/STOK-6795?focusedCommentId=314871
                top: {base: 4, lg: 6},
                // use inset instead of right to handle rtl languages
                insetEnd: {base: 4, lg: 6},
                // negative margin and box size based on the new foundation close button click area
                // see https://www.figma.com/file/95ObDheGIHKVlOVY8JPVCL/STOKKE-PWA?node-id=22423%3A181004&t=OieFToV97KDxd2IX-4
                margin: '-13px',
                boxSize: 10.5,
                // font size determines icon size which is defined as 1em/1em
                fontSize: 'md',
                _hover: {
                    bg: 'stokkeCore.black',
                    color: 'stokkeCore.white'
                }
            },
            body: {
                px: {base: 4, lg: 6, xl: 8},
                py: null,
                ...textStyles['Regular Medium']
            },
            footer: {
                px: {base: 4, lg: 6, xl: 8},
                py: null
            }
        }
    },
    variants: {
        auth: {
            body: {
                paddingBottom: 12,
                marginTop: 16,
                paddingTop: {lg: 2}
            }
        },
        cart: {
            dialog: {
                maxW: {lg: '60rem'},
                display: {lg: 'grid'},
                gridTemplateColumns: {
                    base: '1fr',
                    lg: 'minmax(0, 1fr) 23.4375rem'
                },
                gridTemplateRows: {
                    lg: 'auto auto'
                },
                margin: 'auto'
            },
            header: {
                gridColumn: 1,
                marginTop: 16,
                marginBottom: 4,
                // use inherited color
                color: null,
                // same as default but xl same as lg
                px: {xl: null},
                // reset from base style
                fontSize: null,
                fontWeight: null
            },
            body: {
                gridColumn: 1,
                // same as default but xl same as lg
                px: {xl: null}
            },
            footer: {
                gridColumn: 2,
                gridRowStart: 2,
                gridRowEnd: -1,
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'stretch',
                gap: 8,
                // same as default but xl same as lg
                px: {xl: null},
                bg: 'stokkeCore.white',
                width: {lg: '23,4375rem'},
                paddingTop: {base: 6, lg: 0},
                paddingBottom: {base: 12, lg: 6}
            }
        },
        confirm: {
            dialog: {
                pt: 16,
                pb: 12
            },
            header: {
                // same as default but xl same as lg
                px: {xl: null},
                ...textStyles['Regular 45XLarge'],
                // Override because it doesn't change based on breakpoint
                fontSize: '4.5xl'
            },
            body: {
                // same as default but xl same as lg
                px: {xl: null},
                py: 8
            },
            footer: {
                flexDirection: 'column-reverse',
                gap: 8,
                // same as default but xl same as lg
                px: {xl: null}
            }
        },
        plp: {
            dialog: {
                maxWidth: {
                    base: null,
                    lg: '31.25rem'
                },
                maxH: '100%',
                height: '100%',
                my: null,
                // align to the bottom of the screen
                alignSelf: 'flex-end',
                // align to the left of the screen
                marginRight: 'auto'
            },
            header: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                bg: 'stokkeCore.white',
                paddingTop: {base: 6, lg: 8, xl: 10},
                paddingBottom: {base: 2.5, lg: 6},
                gap: 2
            },
            footer: {
                display: 'block',
                width: 'full',
                bg: 'stokkeCore.white',
                paddingY: {base: 4, xl: 10},
                // use color to colorize the border as borderColor doesn't work
                color: 'stokkeGray.mid',
                borderTop: '1px solid'
            }
        },
        pdp: {
            dialog: {
                pt: 16,
                pb: 10
            },
            body: {
                px: {base: 6, xl: 8}
            }
        },
        productView: {
            dialog: {
                pt: {base: 14, lg: 12},
                pb: {base: 8, lg: 6}
            },
            body: {
                backgroundColor: 'stokkeCore.white',
                px: {base: 4, xl: 12},
                // extend color variation list to edge of modal as per design
                '.variation-container-list': {
                    paddingRight: {
                        base: 4,
                        // note: color variation becomes multi-line on lg+
                        lg: 0
                    },
                    marginRight: {
                        base: -4,
                        lg: 0
                    }
                }
            }
        },
        generic: {
            dialog: {
                pt: 14,
                pb: 10,
                px: {base: 4, xl: 6}
            },
            header: {
                px: 0,
                color: 'stokkeCore.black',
                ...textStyles['Regular 4XLarge']
            },
            body: {
                px: 0,
                pt: 6
            }
        },
        bonusOffer: {
            header: {
                padding: 4,
                paddingRight: 14
            },
            dialog: {
                margin: 'auto'
            },
            body: {
                paddingX: 0,
                pt: 12,
                pb: {base: 8, lg: 12},
                // extend color variation list to edge of modal as per design
                '.variation-container-list': {
                    paddingRight: {
                        base: 4,
                        // note: color variation becomes multi-line on lg+
                        lg: 0
                    },
                    marginRight: {
                        base: -4,
                        lg: 0
                    }
                }
            },
            closeButton: {
                top: {base: 5, lg: 5},
                insetEnd: {base: 5, lg: 5},
                margin: '-14px'
            }
        },
        engraving: {
            dialog: {
                bg: 'stokkeGray.bright'
            },
            header: {
                flex: '4rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                color: 'unset'
            },
            body: {
                paddingInlineStart: {base: 4, xl: 6},
                paddingInlineEnd: {base: 4, xl: 6},
                pb: 12,

                '.engraving-modal-image-container': {
                    width: '100%',
                    height: '12.5rem',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover'
                }
            },
            closeButton: {
                top: 'unset',
                right: 'unset'
            }
        },
        reviews: {
            header: {
                ...textStyles['Regular 4XLarge'],
                pt: 6,
                px: 0,
                pb: 8
            },
            dialogContainer: {
                alignItems: 'flex-start !important'
            },
            dialog: {
                mx: 'auto',
                padding: 4,
                mt: {
                    base: 8,
                    lg: 10
                },
                mb: {
                    lg: 10
                }
            },
            body: {
                px: 0
            },
            closeButton: {
                top: {base: 5, lg: 5},
                insetEnd: {base: 5, lg: 5},
                margin: '-14px'
            }
        },
        countrySelector: {
            header: {
                textStyles: 'Regular Medium'
            },
            dialog: {
                padding: {
                    base: '1rem',
                    md: '1.5rem'
                }
            },
            body: {
                padding: 0
            }
        },
        reviewsFilterSortOverlay: {
            header: {
                display: 'flex',
                flexDirection: 'column',
                pt: 6,
                px: 4,
                pb: 4,
                gap: 2
            },
            footer: {
                justifyContent: 'center',
                px: 4,
                py: 4,
                borderTop: '1px solid',
                borderColor: 'stokkeGray.mid'
            }
        },
        fanreel: {
            dialog: {
                maxH: {
                    base: '100%',
                    md: 'calc(100% - 5rem)'
                },
                maxW: {
                    base: '100%',
                    md: '25rem',
                    lg: '60.9375rem' // 975px
                },
                height: {
                    base: '100%',
                    lg: '37.5rem'
                },
                my: {
                    base: 0,
                    md: 10
                },
                minH: {base: '100vh', md: 'unset'},
                position: {base: 'fixed', md: 'relative'},
                top: {base: 0, md: 'unset'},
                left: {base: 0, md: 'unset'},
                right: {base: 0, md: 'unset'},
                bottom: {base: 0, md: 'unset'}
            },
            body: {
                overflow: 'hidden',
                display: 'flex',
                flexDirection: {base: 'column', lg: 'row'},
                px: null
            },
            closeButton: {
                top: 0,
                insetEnd: 0,
                margin: 0,
                boxSize: 11.25
            }
        },
        certificate: {
            dialog: {
                minWidth: {base: '100%', lg: '41.375rem'},
                backgroundColor: 'stokkeCore.white'
            },
            closeButton: {
                // Override the default close button positions for all breakpoints
                top: {base: '1rem', md: '1rem', lg: '1.5rem', xl: '1.5rem', '2xl': '1.5rem'},
                right: {base: '1rem', md: '1rem', lg: '1.5rem', xl: '1.5rem', '2xl': '1.5rem'}
            },
            body: {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                height: '100%',
                // Override the default padding values for all breakpoints
                paddingInlineStart: {
                    base: '1rem',
                    md: '1rem',
                    lg: '1.5rem',
                    xl: '1.5rem',
                    '2xl': '1.5rem'
                },
                paddingInlineEnd: {
                    base: '1rem',
                    md: '1rem',
                    lg: '1.5rem',
                    xl: '1.5rem',
                    '2xl': '1.5rem'
                },
                paddingTop: {
                    base: '1rem',
                    lg: '1.5rem',
                    md: '1.5rem',
                    xl: '1.5rem',
                    '2xl': '1.5rem'
                },
                paddingBottom: {
                    base: '2.5rem',
                    lg: '3rem',
                    md: '3rem',
                    xl: '3rem',
                    '2xl': '3rem'
                }
            }
        },
        setProduct: {
            body: {
                px: null,
                paddingInline: {base: 4, lg: 12},
                paddingTop: 12,
                paddingBottom: {
                    base: 8,
                    lg: 12
                }
            }
        }
    },
    sizes: {
        lg: {
            // 960px
            dialog: {
                maxW: '60rem'
            }
        },
        xl: {
            // 992px
            dialog: {
                maxW: {lg: '62rem'}
            }
        },
        full: {
            dialog: {
                maxW: '100%',
                height: '100%'
            },
            body: {
                // needed for zoom modal to fill vertically available space
                // might need to be moved as separate variant
                display: 'flex',
                flexDirection: 'column',
                mx: 'auto',
                // limit the width to not go over 1536px
                maxWidth: 'container.xxl',
                // limit the body height to viewport - needed for zoom modal to prevent scrolling
                maxHeight: `calc(100vh - var(--chakra-space-24))`
            }
        },
        navigation: {
            dialog: {
                maxW: '100%',
                maxH: '100%'
            }
        }
    },
    defaultProps: {
        colorScheme: 'stokkePink'
    }
}
